import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import OptimizedButton from "../../../components/agent/UX/OptimizedButton";
import LoadingSpinner from "../../../components/agent/UX/LoadingSpinner";
// Removed usePixieDustNavigation - SalesPipeline navigates to trip management, not itinerary views
import { cleanupOnNavigation } from "../../../utils/tabCleanup";
import useErrorHandler from "../../../hooks/useErrorHandler";
import useOptimizedFetch from "../../../hooks/useOptimizedFetch";
import {
  Search,
  Filter,
  Plus,
  MoreVertical,
  Calendar,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Target,
  TrendingUp,
  Eye,
  Edit3,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Star,
  Sparkles,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Settings,
  Download,
  Upload,
  FileText,
  ChevronDown,
  ChevronUp,
  X,
  CheckSquare,
  Square,
  List,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "../../../styles/modern-design-system.css";
import "../../../styles/trip-cards.css";
import toast from "../../../utils/toast";
import { decodeHtmlEntities } from "../../../utils/textUtils";

const ModernSalesPipeline = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Note: SalesPipeline navigates to trip management, not itinerary views
  // Pixie dust animation is only for itinerary views
  const { handleError } = useErrorHandler();

  // State Management
  const [trips, setTrips] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTrips, setSelectedTrips] = useState([]);
  const [selectedStage, setSelectedStage] = useState("all");
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [selectedDateRange, setSelectedDateRange] = useState("all");
  const [sortBy, setSortBy] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Pipeline stages
  const pipelineStages = [
    { id: "lead", name: "Lead", color: "blue", count: 0 },
    { id: "prospect", name: "Prospect", color: "yellow", count: 0 },
    { id: "quoted", name: "Quoted", color: "orange", count: 0 },
    { id: "negotiating", name: "Negotiating", color: "purple", count: 0 },
    { id: "closed_won", name: "Closed Won", color: "green", count: 0 },
    { id: "closed_lost", name: "Closed Lost", color: "red", count: 0 },
  ];

  // Fetch data
  const { data: tripsData, loading: tripsLoading, error: tripsError } = useOptimizedFetch(
    "/api/trips",
    "trips",
    { method: "GET" }
  );

  const { data: clientsData, loading: clientsLoading, error: clientsError } = useOptimizedFetch(
    "/api/clients",
    "clients", 
    { method: "GET" }
  );

  useEffect(() => {
    if (tripsData) {
      setTrips(tripsData);
    }
    if (clientsData) {
      setClients(clientsData);
    }
  }, [tripsData, clientsData]);

  useEffect(() => {
    setLoading(tripsLoading || clientsLoading);
  }, [tripsLoading, clientsLoading]);

  useEffect(() => {
    if (tripsError || clientsError) {
      handleError(tripsError || clientsError);
    }
  }, [tripsError, clientsError, handleError]);

  // Combine trips with client data
  const enrichedTrips = trips.map(trip => {
    const client = clients.find(c => c.clientid === trip.clientid);
    return {
      ...trip,
      client: client || null,
      stage: trip.status === 'pending' ? 'prospect' : 
             trip.status === 'confirmed' ? 'quoted' : 
             trip.status === 'completed' ? 'closed_won' : 'lead'
    };
  });

  // Filter and sort trips
  const filteredTrips = enrichedTrips
    .filter(trip => {
      const matchesSearch = searchQuery === "" || 
        trip.tripname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trip.client?.fullname?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStage = selectedStage === "all" || trip.stage === selectedStage;
      
      return matchesSearch && matchesStage;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case "created":
          aValue = new Date(a.createdat);
          bValue = new Date(b.createdat);
          break;
        case "tripname":
          aValue = a.tripname?.toLowerCase() || "";
          bValue = b.tripname?.toLowerCase() || "";
          break;
        case "client":
          aValue = a.client?.fullname?.toLowerCase() || "";
          bValue = b.client?.fullname?.toLowerCase() || "";
          break;
        case "stage":
          aValue = a.stage;
          bValue = b.stage;
          break;
        default:
          aValue = new Date(a.createdat);
          bValue = new Date(b.createdat);
      }
      
      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Pagination
  const totalPages = Math.ceil(filteredTrips.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTrips = filteredTrips.slice(startIndex, startIndex + itemsPerPage);

  // Stage counts
  const stageCounts = pipelineStages.map(stage => ({
    ...stage,
    count: enrichedTrips.filter(trip => trip.stage === stage.id).length
  }));

  const getStageColor = (stageId) => {
    const stage = pipelineStages.find(s => s.id === stageId);
    return stage?.color || "gray";
  };

  const getStageName = (stageId) => {
    const stage = pipelineStages.find(s => s.id === stageId);
    return stage?.name || "Unknown";
  };

  const handleStageChange = (tripId, newStage) => {
    // Update trip stage in local state
    setTrips(prevTrips => 
      prevTrips.map(trip => 
        trip.tripid === tripId ? { ...trip, stage: newStage } : trip
      )
    );
    
    toast.success(`Trip moved to ${getStageName(newStage)} stage`);
  };

  const handleBulkAction = (action) => {
    if (selectedTrips.length === 0) {
      toast.error("Please select trips first");
      return;
    }
    
    switch (action) {
      case "delete":
        // Handle bulk delete
        toast.success(`Deleted ${selectedTrips.length} trips`);
        break;
      case "export":
        // Handle bulk export
        toast.success(`Exported ${selectedTrips.length} trips`);
        break;
      default:
        toast.info(`Bulk action: ${action}`);
    }
    
    setSelectedTrips([]);
    setShowBulkActions(false);
  };

  const handleSelectTrip = (tripId) => {
    setSelectedTrips(prev => 
      prev.includes(tripId) 
        ? prev.filter(id => id !== tripId)
        : [...prev, tripId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTrips.length === paginatedTrips.length) {
      setSelectedTrips([]);
    } else {
      setSelectedTrips(paginatedTrips.map(trip => trip.tripid));
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Sales Pipeline</h1>
                  <p className="mt-2 text-gray-600">
                    Manage your sales opportunities and track progress
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <OptimizedButton
                    onClick={() => navigate("/agent/crm/trips")}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    New Trip
                  </OptimizedButton>
                </div>
              </div>
            </div>

            {/* Pipeline Overview */}
            <div className="mb-8">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                {stageCounts.map((stage) => (
                  <div
                    key={stage.id}
                    className={`bg-white rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                      selectedStage === stage.id 
                        ? `border-${stage.color}-500 bg-${stage.color}-50` 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedStage(selectedStage === stage.id ? "all" : stage.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full bg-${stage.color}-500 mr-2`}></div>
                        <span className="text-sm font-medium text-gray-900">{stage.name}</span>
                      </div>
                      <span className={`text-lg font-bold text-${stage.color}-600`}>
                        {stage.count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mb-6">
              <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search trips or clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Filter Toggle */}
                <OptimizedButton
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Filter className="h-4 w-4" />
                  <span>Filters</span>
                  {showFilters ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </OptimizedButton>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sort By
                      </label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="created">Created Date</option>
                        <option value="tripname">Trip Name</option>
                        <option value="client">Client Name</option>
                        <option value="stage">Stage</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Order
                      </label>
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="desc">Descending</option>
                        <option value="asc">Ascending</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Items Per Page
                      </label>
                      <select
                        value={itemsPerPage}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Actions */}
            {selectedTrips.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedTrips.length} trip{selectedTrips.length !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex items-center space-x-2">
                    <OptimizedButton
                      onClick={() => handleBulkAction("export")}
                      className="text-blue-700 hover:bg-blue-100"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Export
                    </OptimizedButton>
                    <OptimizedButton
                      onClick={() => handleBulkAction("delete")}
                      className="text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </OptimizedButton>
                    <OptimizedButton
                      onClick={() => setSelectedTrips([])}
                      className="text-gray-700 hover:bg-gray-100"
                    >
                      <X className="h-4 w-4" />
                    </OptimizedButton>
                  </div>
                </div>
              </div>
            )}

            {/* Trips Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedTrips.length === paginatedTrips.length && paginatedTrips.length > 0}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trip
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stage
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedTrips.map((trip) => (
                      <tr key={trip.tripid} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedTrips.includes(trip.tripid)}
                            onChange={() => handleSelectTrip(trip.tripid)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {decodeHtmlEntities(trip.tripname) || "Unnamed Trip"}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {trip.tripid}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            {trip.client ? (
                              <>
                                <div className="text-sm font-medium text-gray-900">
                                  {decodeHtmlEntities(trip.client.fullname)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {trip.client.email}
                                </div>
                              </>
                            ) : (
                              <span className="text-sm text-gray-400">No client</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={trip.stage}
                            onChange={(e) => handleStageChange(trip.tripid, e.target.value)}
                            className={`text-sm px-2 py-1 rounded-full border-0 focus:ring-2 focus:ring-blue-500 bg-${getStageColor(trip.stage)}-100 text-${getStageColor(trip.stage)}-800`}
                          >
                            {pipelineStages.map((stage) => (
                              <option key={stage.id} value={stage.id}>
                                {stage.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(trip.createdat).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <OptimizedButton
                              onClick={() => navigate(`/agent/crm/trips/${trip.tripid}`)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Eye className="h-4 w-4" />
                            </OptimizedButton>
                            <OptimizedButton
                              onClick={() => navigate(`/agent/crm/trips/${trip.tripid}`)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <Edit3 className="h-4 w-4" />
                            </OptimizedButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <OptimizedButton
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </OptimizedButton>
                    <OptimizedButton
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </OptimizedButton>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">{startIndex + 1}</span> to{" "}
                        <span className="font-medium">
                          {Math.min(startIndex + itemsPerPage, filteredTrips.length)}
                        </span>{" "}
                        of <span className="font-medium">{filteredTrips.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <OptimizedButton
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </OptimizedButton>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          if (pageNum > totalPages) return null;
                          
                          return (
                            <OptimizedButton
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNum
                                  ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                  : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                              }`}
                            >
                              {pageNum}
                            </OptimizedButton>
                          );
                        })}
                        
                        <OptimizedButton
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </OptimizedButton>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Empty State */}
            {filteredTrips.length === 0 && (
              <div className="text-center py-12">
                <Sparkles className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No trips found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery ? "Try adjusting your search criteria." : "Get started by creating a new trip."}
                </p>
                <div className="mt-6">
                  <OptimizedButton
                    onClick={() => navigate("/agent/crm/trips")}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    New Trip
                  </OptimizedButton>
                </div>
              </div>
            )}
          </div>
    </div>
  );
};

export default ModernSalesPipeline;
